<template>
    <div class="ActivityHistoryTab">
        <ActivityHistoryTable
            :entries="status.list"
            :loading="status.loading"
            :finished="status.finished"
            :filters="filters"
            :client-tags="status.actionableTags.clientTags"
            :system-tags="status.actionableTags.systemTags"
            :client-tags-map="clientTagsMap"
            :system-tags-map="systemTagsMap"
            :enum-labels="enumLabels"
            :requirement-definitions="requirementDefinitions"
            :requirement-instance-models="requirementInstanceModels"
            :active-verification-ids="activeVerificationIds"
            :criterion-titles="criterionTitles"
            @scroll-end="loadNext"
            @filter-change="applyFilters"
            @view="onView"
        />
    </div>
</template>

<style lang="scss">
    .ActivityHistoryTab {
        display: flex;
        flex-direction: column;
        height: 100%;
        padding: 20px 28px 28px;
        box-sizing: border-box;
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { ActivityHistoryEntry, ActivityHistoryQuery, ActivityHistorySystemTags, ActivityHistoryTagsMap, ActivityType, EntityActionRevertedDetails, EntityPropertyChangedDetails, EvalOverrideDetails, NotificationDetails, ResourceType, ResponseOutcome, ResponseReceivedDetails } from '@evidentid/tprm-portal-lib/models/activity-history';
    import {
        TprmRequirementCategory,
        TprmRequirementDefinition,
    } from '@evidentid/tprm-portal-lib/models/common';
    import { TprmRequirementInstanceModel } from '@evidentid/tprm-portal-lib/models/common/TprmRequirementInstanceModel.model';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { EntityRequirementDetails, TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';

    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { EnumCategories } from '@/modules/dashboard/models/EnumCategories.model';
    import { useEntityDetailsStore } from '@/modules/entity-details/store';
    import { DOCUMENT_STILL_PROCESSING_MESSAGE } from '@/modules/entity-events/constants/documentRetrieval.constants';
    import { ActivityHistoryFilters } from '@/modules/entity-events/models/ActivityHistoryFilters.model';
    import { ActivityHistoryStatus, useEntityEventsStore } from '@/modules/entity-events/store';
    import { formatDateOnly } from '@/modules/entity-events/utils/formatDateOnly';
    import { getExceptionTypeLabel } from '@/modules/entity-events/utils/getExceptionTypeLabel';
    import { humanizeCriterionField } from '@/modules/entity-events/utils/humanizeCriterionField';
    import { normalizeActivityHistoryFilters } from '@/modules/entity-events/utils/normalizeActivityHistoryFilters';
    import { useEntityTagsStore } from '@/modules/entity-tags/store';
    import { formatDateTime } from '@/utils/formatDateTime';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';

    import ActivityHistoryTable from '../ActivityHistoryTable/ActivityHistoryTable.vue';

    export default defineComponent({
        name: 'ActivityHistoryTab',
        components: { ActivityHistoryTable },
        props: {
            entity: {
                type: Object as PropType<Entity>,
                required: true,
            },
            rpName: {
                type: String,
                required: true,
            },
            active: {
                type: Boolean,
                default: false,
            },
            selectedFilters: {
                type: Object as PropType<ActivityHistoryFilters>,
                default: (): ActivityHistoryFilters => ({ entryType: [], tags: [], causedBy: '' }),
            },
            activeVerificationIds: {
                type: Array as PropType<string[]>,
                default: (): string[] => [] as string[],
            },
        },
        emits: [ 'filter-change' ],
        data() {
            return {
                initialized: false,
            };
        },
        computed: {
            stateKey(): string {
                return `${this.rpName}:${this.entity.id}`;
            },
            status(): ActivityHistoryStatus {
                return useEntityEventsStore().activityHistory[this.stateKey] || {
                    loading: false,
                    finished: false,
                    list: [],
                    filters: {},
                    actionableTags: { clientTags: [], systemTags: [] },
                };
            },
            filters(): ActivityHistoryFilters {
                return normalizeActivityHistoryFilters(this.status.filters);
            },
            enumLabels(): CategorizedEnumLabels {
                return useDashboardStore().categorizedEnumLabels || {};
            },
            requirementDefinitions(): Record<TprmRequirementType, TprmRequirementDefinition> {
                return useDashboardStore().tprmRequirementsDefinitions || {};
            },
            requirementInstanceModels(): Record<TprmRequirementCategory, TprmRequirementInstanceModel[]> {
                return useDashboardStore().requirementInstanceModels || {};
            },
            clientTagsMap(): ActivityHistoryTagsMap | null {
                return useEntityTagsStore().tagsStatus?.[this.rpName]?.tags || null;
            },
            systemTagsMap(): ActivityHistorySystemTags | null {
                return useEntityTagsStore().systemTagsStatus?.tags || null;
            },
            criterionTitles(): Record<string, string> {
                // Resolve criterion field paths to their configured titles from the entity's own
                // evaluation results (already loaded for the open entity, keyed by RP) — reliable and
                // country-independent. Each evaluation result pairs its used field path(s) with the
                // human-readable criterionName. Exception rows fall back to a humanized field label on a miss.
                const requirementDetails: EntityRequirementDetails[] =
                    useEntityDetailsStore().requirementDetailsListStatus?.[this.rpName]?.list || [];
                const titlesByField: Record<string, string> = {};
                requirementDetails.forEach((requirement: EntityRequirementDetails) => {
                    (requirement.evaluationResults || []).forEach((result) => {
                        if (!result.criterionName) {
                            return;
                        }
                        (result.usedFields || []).forEach((field: string) => {
                            if (field) {
                                titlesByField[field] = result.criterionName;
                            }
                        });
                    });
                });
                return titlesByField;
            },
        },
        watch: {
            active: {
                immediate: true,
                handler(isActive: boolean) {
                    if (isActive && !this.initialized) {
                        this.initialized = true;
                        this.load(this.selectedFilters);
                    }
                },
            },
            'entity.id'() {
                this.initialized = false;
                if (this.active) {
                    this.initialized = true;
                    this.load(this.selectedFilters);
                }
            },
            selectedFilters(newFilters: ActivityHistoryFilters): void {
                if (this.active) {
                    this.load(newFilters);
                }
            },
        },
        beforeUnmount() {
            useEntityEventsStore().clearActivityHistory({
                rpName: this.rpName,
                entityId: this.entity.id,
            });
        },
        methods: {
            load(filters?: ActivityHistoryFilters): void {
                useEntityEventsStore().loadActivityHistory({
                    rpName: this.rpName,
                    entityId: this.entity.id,
                    filters: filters ?? this.selectedFilters,
                });
                useEntityTagsStore().loadTags({ rpName: this.rpName });
                useEntityTagsStore().loadSystemTags();
            },
            loadNext(): void {
                useEntityEventsStore().loadNextActivityHistory({
                    rpName: this.rpName,
                    entityId: this.entity.id,
                });
            },
            applyFilters(filters: ActivityHistoryQuery): void {
                useEntityEventsStore().loadActivityHistory({
                    rpName: this.rpName,
                    entityId: this.entity.id,
                    filters,
                });
                this.$emit('filter-change', filters);
            },
            onView(entry: ActivityHistoryEntry): void {
                if (entry.resourceType === ResourceType.NOTE) {
                    // A NOTE row always carries the note's id; the check only guards against the
                    // type going nullable for global rows, which never have this resourceType.
                    if (entry.resourceId == null) {
                        return;
                    }
                    (this as any).$procedures.execute('viewEntityNoteProcedure', {
                        rpName: this.rpName,
                        entityId: this.entity.id,
                        noteId: entry.resourceId,
                        initialTags: entry.clientTags,
                    });
                } else if (entry.activityType === ActivityType.EVAL_OVERRIDE_GRANTED) {
                    this.viewException(entry);
                } else if (entry.resourceType === ResourceType.EVAL_OVERRIDE) {
                    const details = entry.details as EvalOverrideDetails | null;
                    if (details?.note == null) {
                        return;
                    }
                    (this as any).$procedures.execute('viewNoteProcedure', {
                        noteText: details.note,
                        addedBy: entry.causedBy,
                        date: formatDateTime(entry.createdAt, 'MMM D, YYYY [at] h:mm a'),
                    });
                } else if (entry.activityType === ActivityType.ENTITY_ACTION_REVERTED) {
                    const details = entry.details as EntityActionRevertedDetails | null;
                    if (details?.revertReason == null) {
                        return;
                    }
                    (this as any).$procedures.execute('viewNoteProcedure', {
                        noteText: details.revertReason,
                        addedBy: entry.causedBy,
                        date: formatDateTime(entry.createdAt, 'MMM D, YYYY [at] h:mm a'),
                    });
                } else if (entry.resourceType === ResourceType.DOCUMENT) {
                    // A DOCUMENT row always carries the document's id; the check only guards
                    // against the type going nullable for global rows, which never have this
                    // resourceType.
                    if (entry.resourceId == null) {
                        return;
                    }
                    (this as any).$procedures.execute('viewEntityDocumentProcedure', {
                        rpName: this.rpName,
                        entityId: this.entity.id,
                        documentId: entry.resourceId,
                        initialTags: entry.clientTags,
                    });
                } else if (
                    (
                        [ ActivityType.EMAIL_SENT, ActivityType.EMAIL_ERROR ] as ActivityType[]
                    ).includes(entry.activityType) &&
                    entry.details
                ) {
                    (this as any).$procedures.execute('viewNotificationDetailProcedure', {
                        details: entry.details as NotificationDetails,
                    });
                } else if (entry.activityType === ActivityType.RESPONSE_RECEIVED) {
                    const details = entry.details as ResponseReceivedDetails;
                    // A DOCUMENT row expects a file, so an empty lookup means it's still being stored.
                    const documentExpected = details.outcome === ResponseOutcome.DOCUMENT;
                    (this as any).$procedures.execute('showEntityRequirementRprDocuments', {
                        rpName: this.rpName,
                        email: this.entity.contactEmail || '',
                        requirementType: details.coverageType as TprmRequirementType,
                        requirementInstanceId: details.instanceId || null,
                        entityId: this.entity.id,
                        verificationId: details.verificationId,
                        ...(documentExpected && {
                            customNoDocMsg: DOCUMENT_STILL_PROCESSING_MESSAGE,
                            documentExpected: true,
                        }),
                    });
                } else if (entry.activityType === ActivityType.ENTITY_PROPERTY_CHANGED) {
                    const details = entry.details as EntityPropertyChangedDetails | null;
                    if (!details?.changedPropertiesDisplayNames?.length) {
                        return;
                    }
                    (this as any).$procedures.execute('viewEntityPropertyChangedProcedure', {
                        changedPropertiesDisplayNames: details.changedPropertiesDisplayNames,
                    });
                }
            },
            viewException(entry: ActivityHistoryEntry): void {
                const details = entry.details as EvalOverrideDetails | null;
                const coverageType = details?.coverageType;
                const coverageLabels = this.enumLabels[EnumCategories.coverageTypes] || {};
                const coverageTypeLabel = coverageType
                    ? coverageLabels[coverageType]?.label || coverageType
                    : '';
                const criterionLabel = details?.criterionField
                    ? this.criterionTitles[details.criterionField] || humanizeCriterionField(details.criterionField)
                    : '';
                (this as any).$procedures.execute('viewExceptionProcedure', {
                    coverageTypeLabel,
                    criterionLabel,
                    overrideTypeLabel: getExceptionTypeLabel(details?.expirationType),
                    expirationDate: formatDateOnly(details?.expirationDate),
                    note: details?.note ?? null,
                    grantedBy: entry.causedBy,
                    date: formatDateTime(entry.createdAt, 'MMM D, YYYY [at] h:mm a'),
                });
            },
        },
    });
</script>
